import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { ModalWordmarkEditTitle, ModalWordmarkTitle } from './ui/ModalTitle';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Modal } from './ui/Modal';
import { EmptyState } from './ui/EmptyState';
import { SkeletonRow } from './ui/Skeleton';
import { formLabelClass } from './ui/fieldStyles';
import WordStyleColorPicker from './WordStyleColorPicker';
import { moduleForInsightType } from '../permissions/insightModuleMap';
import { useModuleAccess } from '../permissions/useModuleAccess';
import { ModuleAccessBanner } from './ui/ModuleAccessBanner';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

function InsightMaster({
  title,
  modalTitle,
  addButtonText,
  searchPlaceholder,
  nameLabel,
  namePlaceholder,
  itemType,
  hasColorField = false,
}) {
  const moduleKey = moduleForInsightType(itemType);
  const { canCreate, canEdit, canMutate } = useModuleAccess(moduleKey);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [colorCode, setColorCode] = useState('');
  const [colorError, setColorError] = useState('');
  const [formError, setFormError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) {
      h.Authorization = `Bearer ${token}`;
    }
    return h;
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    setPageError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/insights?type=${itemType}`, { headers });
      if (!response.ok) {
        throw new Error(`Unable to load ${title.toLowerCase()}`);
      }
      const data = await response.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setPageError(err?.message || `Unable to load ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemType]);

  useEffect(() => {
    if (!pageMessage) return undefined;
    const timer = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [pageMessage]);

  useEffect(() => {
    if (!pageError) return undefined;
    const timer = setTimeout(() => setPageError(''), 5000);
    return () => clearTimeout(timer);
  }, [pageError]);

  useEffect(() => {
    if (!formError) return undefined;
    const timer = setTimeout(() => setFormError(''), 5000);
    return () => clearTimeout(timer);
  }, [formError]);

  const modalHeaderTitle =
    formMode === 'create'
      ? itemType === 'COLOR'
        ? <ModalWordmarkTitle>color</ModalWordmarkTitle>
        : modalTitle
      : itemType === 'COLOR'
        ? <ModalWordmarkEditTitle>color</ModalWordmarkEditTitle>
        : `Edit ${String(modalTitle).replace(/^Add\s+/, '')}`;

  const filteredItems = items.filter((item) => {
    const key = search.trim().toLowerCase();
    if (!key) return true;
    return String(item.name || '').toLowerCase().includes(key);
  });

  const openCreateForm = () => {
    if (!canCreate) return;
    setFormMode('create');
    setEditingItem(null);
    setName('');
    setNameError('');
    setColorCode('');
    setColorError('');
    setFormError('');
    setShowForm(true);
  };

  const openEditForm = (item) => {
    if (!canEdit) return;
    setFormMode('edit');
    setEditingItem(item);
    setName(String(item.name || ''));
    setNameError('');
    setColorCode(
      (() => {
        const c = item?.colorCode;
        if (c == null || c === '') return '';
        return /^#[0-9A-Fa-f]{6}$/i.test(String(c).trim()) ? String(c).trim().toUpperCase() : '';
      })()
    );
    setColorError('');
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormMode('create');
    setEditingItem(null);
    setName('');
    setNameError('');
    setColorCode('');
    setColorError('');
    setFormError('');
    setSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPageMessage('');
    setPageError('');
    setFormError('');
    setNameError('');
    setColorError('');

    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Please enter name');
      return;
    }
    if (hasColorField) {
      const cc = String(colorCode || '').trim();
      if (cc && !/^#[0-9A-Fa-f]{6}$/i.test(cc)) {
        setColorError('Enter a valid #RRGGBB color or use No color');
        return;
      }
    }

    setSaving(true);
    try {
      const isCreate = formMode === 'create';
      const colorPayload =
        hasColorField
          ? (() => {
              const t = String(colorCode || '')
                .trim()
                .toUpperCase();
              return /^#[0-9A-Fa-f]{6}$/.test(t) ? t : null;
            })()
          : undefined;
      const response = await fetch(
        isCreate ? `${API_BASE_URL}/api/insights` : `${API_BASE_URL}/api/insights/${editingItem.id}`,
        {
          method: isCreate ? 'POST' : 'PUT',
          headers,
          body: JSON.stringify({
            type: itemType,
            name: trimmedName,
            ...(hasColorField ? { colorCode: colorPayload } : {}),
          }),
        }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to save record');
      }
      const saved = await response.json();
      if (isCreate) {
        setItems((prev) => [saved, ...prev]);
      } else {
        setItems((prev) => prev.map((item) => (item.id === saved.id ? saved : item)));
      }
      setPageMessage(isCreate ? `${title.slice(0, -1)} created successfully` : `${title.slice(0, -1)} updated successfully`);
      closeForm();
    } catch (err) {
      setFormError(err?.message || 'Unable to save record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteItem) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/insights/${confirmDeleteItem.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to delete record');
      }
      setItems((prev) => prev.filter((item) => item.id !== confirmDeleteItem.id));
      setConfirmDeleteItem(null);
      setPageMessage(`${title.slice(0, -1)} deleted successfully`);
    } catch (err) {
      setPageError(err?.message || 'Unable to delete record');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <ModuleAccessBanner moduleKey={moduleKey} />
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title={title}
            actions={
              <>
                <div className="w-full sm:w-64">
                  <Input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    leftIcon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    }
                  />
                </div>
                {canCreate && (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={openCreateForm}
                    leftIcon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    }
                  >
                    {addButtonText}
                  </Button>
                )}
              </>
            }
          />
        </CardHeader>
        <CardBody>
          <AlertMessage message={pageMessage} type="success" onClose={() => setPageMessage('')} className="mb-4" />
          <AlertMessage message={pageError} type="error" onClose={() => setPageError('')} className="mb-4" />
          {!loading && filteredItems.length === 0 ? (
            <EmptyState
              title={search ? 'No matches found' : `No ${String(title).toLowerCase()} yet`}
              description={
                search
                  ? 'Try a different search term.'
                  : canCreate
                    ? 'Create your first record to get started.'
                    : 'No records to display.'
              }
              action={!search && canCreate ? (
                <Button type="button" variant="primary" onClick={openCreateForm}>
                  {addButtonText}
                </Button>
              ) : null}
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th className="w-14">#</th>
                    {hasColorField && <th className="min-w-[11rem]">Color</th>}
                    <th>Name</th>
                    <th>Created</th>
                    <th>Updated</th>
                    {canMutate && <th className="text-right pr-6">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <SkeletonRow key={i} columns={(hasColorField ? 5 : 4) + (canMutate ? 1 : 0)} />
                      ))
                    : filteredItems.map((item, index) => (
                        <tr key={item.id}>
                          <td className="text-slate-400 tabular-nums">{index + 1}</td>
                          {hasColorField && (
                            <td>
                              <div className="flex items-center gap-2">
                                {item.colorCode && /^#[0-9A-Fa-f]{6}$/i.test(String(item.colorCode)) ? (
                                  <span
                                    className="inline-block w-8 h-5 rounded-md border border-slate-200 shadow-inner"
                                    style={{ backgroundColor: item.colorCode }}
                                    title={item.colorCode}
                                  />
                                ) : (
                                  <span
                                    className="inline-block w-8 h-5 rounded-md border border-slate-200"
                                    style={{
                                      backgroundImage:
                                        'repeating-linear-gradient(45deg, #cbd5e1 0, #cbd5e1 2px, #f1f5f9 2px, #f1f5f9 4px)',
                                    }}
                                    title="No color"
                                    aria-label="No color"
                                  />
                                )}
                                <span
                                  className={
                                    item.colorCode && /^#[0-9A-Fa-f]{6}$/i.test(String(item.colorCode))
                                      ? 'min-w-0 max-w-[10rem] truncate font-mono text-xs text-slate-500 tabular-nums'
                                      : 'text-xs text-slate-400 tabular-nums'
                                  }
                                >
                                  {item.colorCode && /^#[0-9A-Fa-f]{6}$/i.test(String(item.colorCode))
                                    ? String(item.colorCode)
                                    : '-'}
                                </span>
                              </div>
                            </td>
                          )}
                          <td className="font-medium text-slate-800">{item.name || '-'}</td>
                          <td className="text-slate-500">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</td>
                          <td className="text-slate-500">{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '—'}</td>
                          {canMutate && (
                            <td className="pr-6">
                              <div className="flex items-center justify-end gap-1.5">
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() => openEditForm(item)}
                                    className="icon-action icon-action-primary"
                                    title="Edit"
                                    aria-label={`Edit ${item.name}`}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                )}
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDeleteItem(item)}
                                    className="icon-action icon-action-danger"
                                    title="Delete"
                                    aria-label={`Delete ${item.name}`}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={showForm}
        onClose={closeForm}
        size="lg"
        title={modalHeaderTitle}
        ariaLabel={itemType === 'COLOR' ? (formMode === 'create' ? 'Add color' : 'Edit color') : undefined}
        description={formMode === 'edit' ? 'Update this record below.' : undefined}
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        }
        footer={
          <>
            <Button type="button" variant="secondary" onClick={closeForm} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" form="insight-form" variant="primary" loading={saving} disabled={saving}>
              {formMode === 'create' ? 'Save' : 'Update'}
            </Button>
          </>
        }
      >
        <form id="insight-form" onSubmit={handleSubmit} className="py-6 space-y-5">
          <AlertMessage message={formError} type="error" onClose={() => setFormError('')} />
          <Input
            label={nameLabel}
            placeholder={namePlaceholder}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            error={nameError}
            required
            autoFocus
          />
          {hasColorField && (
            <div>
              <label className={formLabelClass}>Color (optional)</label>
              <div className="flex flex-wrap items-center gap-3 mb-3 mt-1">
                {colorCode && /^#[0-9A-Fa-f]{6}$/i.test(String(colorCode).trim()) ? (
                  <span
                    className="inline-block w-12 h-10 rounded-xl border border-slate-200 shadow-inner"
                    style={{ backgroundColor: colorCode }}
                    aria-hidden="true"
                  />
                ) : (
                  <span
                    className="inline-block w-12 h-10 rounded-xl border border-slate-200"
                    style={{
                      backgroundImage:
                        'repeating-linear-gradient(45deg, #cbd5e1 0, #cbd5e1 2px, #f1f5f9 2px, #f1f5f9 4px)',
                    }}
                    title="No color"
                    aria-hidden="true"
                  />
                )}
                <span className="font-mono text-sm text-slate-600 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200">
                  {colorCode && /^#[0-9A-Fa-f]{6}$/i.test(String(colorCode).trim()) ? String(colorCode).toUpperCase() : '—'}
                </span>
              </div>
              <div className="max-w-sm animate-scale-in">
                <WordStyleColorPicker
                  value={colorCode}
                  onChange={(v) => {
                    setColorCode(v);
                    if (colorError) setColorError('');
                  }}
                  onClearError={() => {
                    if (colorError) setColorError('');
                  }}
                />
              </div>
              {colorError && <p className="mt-1.5 text-sm text-rose-600">{colorError}</p>}
            </div>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDeleteItem)}
        title="Delete Record"
        message={confirmDeleteItem ? `Do you want to delete "${confirmDeleteItem.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteItem(null)}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
}

export default InsightMaster;
